import React from "react";
import "./App.css";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { MotionConfig, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";

// Import Section Components
import Navigation from "./components/Navigation";
import HeroSection from "./sections/Hero";
import TrustMetrics from "./sections/TrustMetrics";
import ServicesSection from "./sections/Services";
import GlobalPresence from "./sections/GlobalPresence";
import MediaSection from "./sections/Media";
import StoriesSection from "./sections/Stories";
import VenturesSection from "./sections/Ventures";
import ContactSection from "./sections/Contact";
import ToolsTeaser from "./sections/ToolsTeaser";
import CookieBanner from "./components/CookieBanner";
import Footer from "./components/Footer";
import { loadClarity } from "./utils/clarity";
import Seo from "./components/Seo";

// Import Pages
import AI from "./pages/AI";
import Strategie from "./pages/Strategie";
import Digital from "./pages/Digital";
import Taxes from "./pages/Taxes";
import Finances from "./pages/Finances";
import Insights from "./pages/Insights";
import Tools from "./pages/Tools";
import KiVertrieb from "./pages/KiVertrieb";
import Buecher from "./pages/Buecher";
import KiVersicherung from "./pages/KiVersicherung";
import ManagementDashboards from "./pages/ManagementDashboards";
import GoogleAiSearchMythbusting from "./pages/insights/GoogleAiSearchMythbusting";
import KiAdoptionMittelstand from "./pages/insights/KiAdoptionMittelstand";
// Standdepot2027Vorlauf — OFFLINE 2026-05-29: Faktencheck deckte erfundenen BVerfG-Beschluss
// + falsche Begriffe (AVRG/Standdepot) + falsches HNWI-Framing auf. Muss neu geschrieben werden.
// import Standdepot2027Vorlauf from "./pages/insights/Standdepot2027Vorlauf";
import LlmToolLandschaft2026 from "./pages/insights/LlmToolLandschaft2026";
import LlmsErklartFurEntscheider from "./pages/insights/LlmsErklartFurEntscheider";
import EuAiAct2026 from "./pages/insights/EuAiAct2026";
import DsgvoArt35Ki from "./pages/insights/DsgvoArt35Ki";
import PromptEngineeringMittelstand from "./pages/insights/PromptEngineeringMittelstand";
import McpErklaert from "./pages/insights/McpErklaert";
import RagImMittelstand from "./pages/insights/RagImMittelstand";
import OpenSourceVsFrontier from "./pages/insights/OpenSourceVsFrontier";
import CliAiTools from "./pages/insights/CliAiTools";
import ContextEngineering from "./pages/insights/ContextEngineering";
import KiReadinessCheck from "./pages/insights/KiReadinessCheck";
import KiImVertrieb from "./pages/insights/KiImVertrieb";
import KiChatbotsMittelstand from "./pages/insights/KiChatbotsMittelstand";
import LeadScoringOutbound from "./pages/insights/LeadScoringOutbound";
import MeinKiSystem from "./pages/insights/MeinKiSystem";
import ModellLandschaftSommer2026 from "./pages/insights/ModellLandschaftSommer2026";
import ArbeitsagentenStattChat from "./pages/insights/ArbeitsagentenStattChat";
import OpenWeightSommer2026 from "./pages/insights/OpenWeightSommer2026";
import KiOrchestrierteAngriffe from "./pages/insights/KiOrchestrierteAngriffe";
import KiAgentenVersicherungsvertrieb from "./pages/insights/KiAgentenVersicherungsvertrieb";

// Home Page Component
const HomePage = () => {
  const { i18n } = useTranslation();
  const isGerman = (i18n.language || "de").startsWith("de");
  return (
    <div className="font-inter">
      {/* Skip link for keyboard users — WCAG 2.4.1 AA */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-[#C5A66A] focus:text-[#0F1B2D] focus:px-4 focus:py-2 focus:rounded focus:font-semibold"
      >
        {isGerman ? "Zum Hauptinhalt springen" : "Skip to main content"}
      </a>

      <Navigation />
      <main id="main-content">
        <div id="home">
          <HeroSection />
        </div>

        <div id="about">
          <TrustMetrics />
        </div>
        <div id="expertise">
          <ServicesSection />
        </div>
        <ToolsTeaser />
        <div id="global">
          <GlobalPresence />
        </div>
        <div id="media">
          <MediaSection />
        </div>
        <div id="stories">
          <StoriesSection />
        </div>
        <div id="ventures">
          <VenturesSection />
        </div>
        <div id="contact">
          <ContactSection />
        </div>
      </main>
    </div>
  );
};

// Per-route SEO meta (title / description / self-referential canonical) for the
// prerendered section pages. Articles set their own SEO inside InsightLayout.
// Fixes: every page previously inherited the homepage title/description/canonical.
const ROUTE_META = {
  "/": {
    title: "Daaniyal Khan – KI, Vertrieb & Digitalisierung | DACH",
    description:
      "Daaniyal Khan berät im DACH-Raum zu KI, Vertrieb und Digitalisierung: GenAI im Vertrieb, KI in der Versicherung, Management-Dashboards und Strategie.",
  },
  "/ai": {
    title: "KI & Automatisierung für Unternehmen – Daaniyal Khan",
    description:
      "GenAI, ChatGPT, LLMs, RAG und Automatisierung praktisch im Unternehmen einsetzen: Use Cases, Umsetzung und Governance – von KI-Berater Daaniyal Khan.",
  },
  "/strategie": {
    title: "Strategieberatung für 7-stelliges Wachstum – Daaniyal Khan",
    description:
      "Wachstums- und Vertriebsstrategie für Unternehmer im DACH-Raum: eine 7-jährige Case Study und der 100-Million-Blueprint von Daaniyal Khan.",
  },
  "/digital": {
    title: "Digitalisierung & digitale Transformation – Daaniyal Khan",
    description:
      "Digitale Transformation von Vertrieb und Geschäftsprozessen im DACH-Raum: Roadmap, Automatisierung und Umsetzung von Digitalisierungsberater Daaniyal Khan.",
  },
  "/taxes": {
    title: "Steueroptimierung & Steuerstrukturierung – Daaniyal Khan",
    description:
      "Legale Steueroptimierung und internationale Strukturierung für Unternehmer und vermögende Privatpersonen im DACH-Raum: Hebel, Modelle und Fallstricke.",
  },
  "/finances": {
    title: "Strategische Finanz- & Investitionsberatung – Daaniyal Khan",
    description:
      "Finanzstrategie, Investitionsplanung und Kapitaleffizienz für wachsende Unternehmen im DACH-Raum – von Daaniyal Khan.",
  },
  "/tools": {
    title: "KI-Tools für Vertrieb & Finance – Daaniyal Khan",
    description:
      "Praktische KI-Tools von Daaniyal Khan: Unternehmensbewertung, Fördermittel-Finder und Steueroptimierung – sofort nutzbar für Vertrieb und Finance.",
  },
  "/insights": {
    title: "Insights: KI, Vertrieb & Digitalisierung – Daaniyal Khan",
    description:
      "Fachartikel zu KI im Vertrieb, GenAI, LLMs, RAG, Digitalisierung und EU AI Act – Praxiswissen von Daaniyal Khan für Entscheider im DACH-Raum.",
  },
};

function RouteMeta() {
  const { pathname } = useLocation();
  const meta = ROUTE_META[pathname];
  return meta ? (
    <Seo title={meta.title} description={meta.description} path={pathname} />
  ) : null;
}

// Main App Component
function App() {
  const prefersReducedMotion = useReducedMotion();

  React.useEffect(() => {
    // Load Clarity if user has already accepted cookies
    // Add a small delay to ensure cookie banner has time to check localStorage
    const timer = setTimeout(() => {
      loadClarity();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    // WCAG 2.3.3 — respect user's motion preference for all framer-motion animations
    <MotionConfig reducedMotion={prefersReducedMotion ? "always" : "never"}>
      <Router>
        <RouteMeta />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/ai" element={<AI />} />
          <Route path="/strategie" element={<Strategie />} />
          <Route path="/digital" element={<Digital />} />
          <Route path="/taxes" element={<Taxes />} />
          <Route path="/finances" element={<Finances />} />
          <Route path="/tools" element={<Tools />} />
          <Route path="/ki-vertrieb" element={<KiVertrieb />} />
          <Route path="/buecher" element={<Buecher />} />
          <Route path="/ki-versicherung" element={<KiVersicherung />} />
          <Route path="/management-dashboards" element={<ManagementDashboards />} />
          <Route path="/insights" element={<Insights />} />
          <Route
            path="/insights/google-ai-search-mythbusting"
            element={<GoogleAiSearchMythbusting />}
          />
          <Route
            path="/insights/ki-adoption-mittelstand"
            element={<KiAdoptionMittelstand />}
          />
          <Route
            path="/insights/llm-tool-landschaft-2026"
            element={<LlmToolLandschaft2026 />}
          />
          <Route
            path="/insights/llms-erklaert-fuer-entscheider"
            element={<LlmsErklartFurEntscheider />}
          />
          <Route
            path="/insights/eu-ai-act-2026"
            element={<EuAiAct2026 />}
          />
          <Route
            path="/insights/dsgvo-art-35-ki"
            element={<DsgvoArt35Ki />}
          />
          <Route
            path="/insights/prompt-engineering-mittelstand"
            element={<PromptEngineeringMittelstand />}
          />
          <Route
            path="/insights/mcp-erklaert"
            element={<McpErklaert />}
          />
          <Route
            path="/insights/rag-im-mittelstand"
            element={<RagImMittelstand />}
          />
          <Route
            path="/insights/open-source-vs-frontier"
            element={<OpenSourceVsFrontier />}
          />
          <Route
            path="/insights/cli-ai-tools"
            element={<CliAiTools />}
          />
          <Route
            path="/insights/context-engineering"
            element={<ContextEngineering />}
          />
          <Route
            path="/insights/ki-readiness-check"
            element={<KiReadinessCheck />}
          />
          <Route
            path="/insights/ki-im-vertrieb"
            element={<KiImVertrieb />}
          />
          <Route
            path="/insights/ki-chatbots-mittelstand"
            element={<KiChatbotsMittelstand />}
          />
          <Route
            path="/insights/lead-scoring-outbound"
            element={<LeadScoringOutbound />}
          />
          <Route
            path="/insights/mein-ki-system"
            element={<MeinKiSystem />}
          />
          <Route
            path="/insights/modell-landschaft-sommer-2026"
            element={<ModellLandschaftSommer2026 />}
          />
          <Route
            path="/insights/arbeitsagenten-statt-chat"
            element={<ArbeitsagentenStattChat />}
          />
          <Route
            path="/insights/open-weight-sommer-2026"
            element={<OpenWeightSommer2026 />}
          />
          <Route
            path="/insights/ki-orchestrierte-angriffe"
            element={<KiOrchestrierteAngriffe />}
          />
          <Route
            path="/insights/ki-agenten-versicherungsvertrieb"
            element={<KiAgentenVersicherungsvertrieb />}
          />
        </Routes>
        <Footer />
        <CookieBanner />
      </Router>
    </MotionConfig>
  );
}

export default App;